import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  KeyRound,
  Lightbulb,
  LifeBuoy,
  MapPin,
  Plug,
  PlugZap,
  Radio,
  Unplug,
  Webhook,
  X,
  XCircle,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import ListFilters from "../../components/ui/ListFilters";
import Pagination from "../../components/ui/Pagination";
import Skeleton from "../../components/ui/Skeleton";
import Stat from "../../components/ui/Stat";
import { useToast } from "../../components/ui/Toast";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import { ago, Count, CopyId, CredentialsPill, Pill, Section, StatusPill, WebhookPill, planName, priceText } from "../../components/directory/shared";

/**
 * Every install of every app: who has it, whether they finished setup, and
 * whether it's actually being used.
 *
 * Read-only. Tokens never reach this page, and saved secrets only appear as
 * dots plus their last four characters (see backend routes/appInstalls/admin.js).
 */

const DEFAULTS = { page: 1, limit: 20, appId: "", status: "", level: "", credentials: "", webhook: "", token: "", plan: "", sort: "newest", q: "" };

const accountName = (i) => i.locationName || i.companyName || (i.level === "agency" ? "Agency install" : "Unnamed account");
const accountId = (i) => i.locationId || i.companyId || "";


const InstallDrawer = ({ id, onClose }) => {
  const { installs } = useAppServices();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    installs.Get(id).then(({ response, error: err }) => {
      if (!alive) return;
      if (err || !response) setError(err?.message || "Could not load this install.");
      else setData(response);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const i = data?.install;
  const a = data?.activity;

  // Portalled to <body>: the page wrapper animates with a transform, which
  // would otherwise make this "fixed" panel position against the wrapper and
  // cut it off instead of covering the window.
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Install details">
      <button type="button" className="absolute inset-0 bg-ink-900/30 backdrop-blur-[1px]" onClick={onClose} aria-label="Close" />
      <aside className="relative flex h-full w-full max-w-[34rem] flex-col overflow-hidden bg-ink-50 shadow-2xl animate-fade-in">
        <div className="flex items-start gap-3 border-b border-ink-150 bg-white px-6 py-5">
          {i ? <AppIcon app={{ appName: i.appName, appImage: i.appImage, appId: i.appId }} size="md" /> : <Skeleton className="h-11 w-11" rounded="rounded-xl" />}
          <div className="min-w-0 flex-1">
            {i ? (
              <>
                <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink-900">{accountName(i)}</h2>
                <p className="truncate text-[12.5px] text-ink-500">{i.appName}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <StatusPill install={i} />
                  <Pill tone={i.level === "agency" ? "violet" : "ink"} icon={i.level === "agency" ? Building2 : MapPin}>
                    {i.level === "agency" ? "Agency" : "Location"}
                  </Pill>
                  {i.connected ? <Pill tone="emerald" icon={PlugZap}>Connected</Pill> : <Pill tone="rose" icon={Unplug}>No access token</Pill>}
                </div>
              </>
            ) : (
              <div className="space-y-2 pt-1">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {error ? (
            <p className="m-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
          ) : !data ? (
            <div className="space-y-3 p-6">
              {[0, 1, 2].map((k) => (
                <Skeleton key={k} className="h-24 w-full" rounded="rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 bg-white px-6 py-5 text-[12.5px]">
                {[
                  ["Location ID", i.locationId && <CopyId value={i.locationId} />],
                  ["Company ID", i.companyId && <CopyId value={i.companyId} />],
                  ["Installed", formatDate(i.createdAt)],
                  ["Last change", ago(i.updatedAt)],
                  ["Last activity", i.lastActivityAt ? ago(i.lastActivityAt) : "Never"],
                  [
                    "Plan",
                    i.plan && (
                      <span title={i.plan.planId}>
                        {planName(i.plan)}
                        {priceText(i.plan) && <span className="text-ink-500"> · {priceText(i.plan)}</span>}
                        {i.onTrial && <span className="text-amber-600"> · on trial</span>}
                      </span>
                    ),
                  ],
                  ["Installed by", data.userId && <CopyId value={data.userId} />],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <dt className="text-[11px] font-medium text-ink-400">{k}</dt>
                      <dd className="mt-0.5 truncate text-ink-800">{v}</dd>
                    </div>
                  ))}
              </dl>

              <Section title="Setup" icon={KeyRound}>
                {data.fields.length === 0 ? (
                  <p className="text-[13px] text-ink-500">This app doesn't ask for credentials.</p>
                ) : (
                  <ul className="divide-y divide-ink-150 overflow-hidden rounded-xl border border-ink-200/70 bg-white">
                    {data.fields.map((f) => (
                      <li key={f.key} className="flex items-center gap-3 px-3.5 py-2.5">
                        {f.set ? <CheckCircle2 size={15} className="shrink-0 text-emerald-500" /> : <XCircle size={15} className="shrink-0 text-rose-400" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-ink-800">{f.label}</p>
                          <p className={cx("truncate font-mono text-[11.5px]", f.set ? "text-ink-500" : "text-rose-500")}>{f.set ? f.value : "Not saved"}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-ink-200/70 bg-white px-3.5 py-2.5">
                  <Webhook size={15} className={cx("mt-0.5 shrink-0", i.webhookVerifiedAt ? "text-emerald-500" : i.webhookConfirmedAt ? "text-amber-500" : "text-ink-300")} />
                  <p className="text-[12.5px] leading-relaxed text-ink-600">
                    {i.webhookVerifiedAt
                      ? `Webhook working — first message received ${formatDate(i.webhookVerifiedAt)}.`
                      : i.webhookConfirmedAt
                        ? `The customer marked the webhook as set up on ${formatDate(i.webhookConfirmedAt)}, but no message has arrived yet.`
                        : "No message has arrived through the webhook, and the customer hasn't marked it as set up."}
                  </p>
                </div>
                {data.staleCredentialKeys > 0 && (
                  <p className="mt-2 text-[11.5px] text-ink-400">
                    {data.staleCredentialKeys} saved value{data.staleCredentialKeys === 1 ? " is" : "s are"} for fields this app no longer asks for.
                  </p>
                )}
              </Section>

              <Section title={`Activity · last ${a.days} days`} icon={Activity}>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Count label="Messages" value={a.conversation.ok} />
                  <Count label="Message errors" value={a.conversation.failed} tone="rose" />
                  <Count label="Actions run" value={a.action.ok} />
                  <Count label="Action errors" value={a.action.failed} tone="rose" />
                </div>
                {(a.conversation.warnings || 0) + (a.action.warnings || 0) > 0 && (
                  <p className="mt-2 text-[12px] text-amber-700">
                    {(a.conversation.warnings || 0) + (a.action.warnings || 0)} ignored — replies sent to another of the customer's numbers. Warnings, not errors.
                  </p>
                )}
                {a.recent.length > 0 ? (
                  <ul className="mt-3 divide-y divide-ink-150 overflow-hidden rounded-xl border border-ink-200/70 bg-white">
                    {a.recent.map((r) => (
                      <li key={r._id} className="flex items-start gap-2.5 px-3.5 py-2.5">
                        <span className={cx("mt-1.5 h-2 w-2 shrink-0 rounded-full", r.success ? "bg-emerald-500" : r.level === "warning" ? "bg-amber-500" : "bg-rose-500")} aria-label={r.success ? "Succeeded" : r.level === "warning" ? "Warning" : "Failed"} />
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-2 text-[12.5px]">
                            <span className="font-medium text-ink-800">
                              {r.kind === "action" ? r.action || "Action" : r.direction === "inbound" ? "Message received" : "Message sent"}
                            </span>
                            {r.status && <span className="font-mono text-[11px] text-ink-400">{r.status}</span>}
                            <span className="ml-auto shrink-0 text-[11px] text-ink-400">{ago(r.createdAt)}</span>
                          </p>
                          {r.message && <p className={cx("mt-0.5 line-clamp-2 text-[12px]", r.success ? "text-ink-500" : r.level === "warning" ? "text-amber-700" : "text-rose-600")}>{r.message}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-[13px] text-ink-500">Nothing recorded for this install yet.</p>
                )}
              </Section>

              <Section title="Support" icon={LifeBuoy}>
                {data.tickets.length === 0 ? (
                  <p className="text-[13px] text-ink-500">No tickets from this account.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.tickets.map((t) => (
                      <li key={t._id}>
                        <Link
                          to={`/support?status=all&q=${encodeURIComponent(`#${t.number}`)}`}
                          className="flex items-center gap-2 rounded-lg border border-ink-200/70 bg-white px-3 py-2 text-[12.5px] hover:border-ink-300"
                        >
                          <span className="font-mono text-[11px] text-ink-400">#{t.number}</span>
                          <span className="min-w-0 flex-1 truncate text-ink-800">{t.subject}</span>
                          <Pill tone={t.status === "open" ? "brand" : t.status === "pending" ? "amber" : "ink"}>{t.status}</Pill>
                          <ArrowRight size={12} className="text-ink-300" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Feature requests" icon={Lightbulb}>
                {data.requests.length === 0 ? (
                  <p className="text-[13px] text-ink-500">No feature requests from this account.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.requests.map((r) => (
                      <li key={r._id}>
                        <Link
                          to={`/feature-requests?q=${encodeURIComponent(r.title)}`}
                          className="flex items-center gap-2 rounded-lg border border-ink-200/70 bg-white px-3 py-2 text-[12.5px] hover:border-ink-300"
                        >
                          <span className="min-w-0 flex-1 truncate text-ink-800">{r.title}</span>
                          <span className="shrink-0 text-[11px] text-ink-400">{formatDate(r.createdAt)}</span>
                          <ArrowRight size={12} className="text-ink-300" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

/* -------------------------------------------------------------------- page */

const Installs = () => {
  const { installs } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit, sort: query.sort });
    for (const key of ["appId", "status", "level", "credentials", "webhook", "token", "plan", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await installs.List(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load installs", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.appId, query.status, query.level, query.credentials, query.webhook, query.token, query.plan, query.sort, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  const [plansInUse, setPlansInUse] = useState([]);
  useEffect(() => {
    if (!query.appId) return setPlansInUse([]);
    installs.Plans(query.appId).then(({ response }) => setPlansInUse(Array.isArray(response) ? response : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.appId]);

  useEffect(() => {
    installs.Stats().then(({ response }) => setStats(response || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = Boolean(query.q || query.appId || query.status || query.level || query.credentials || query.webhook || query.token || query.plan);
  const clearFilters = () => setQuery({ q: "", appId: "", status: "", level: "", credentials: "", webhook: "", token: "", plan: "" });
  const closeDrawer = useCallback(() => setOpenId(null), []);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats ? (
          <>
            <Stat label="Active installs" value={stats.active} hint={`${stats.total} all time`} icon={<Plug size={16} />} tone="brand" />
            <Stat label="New this week" value={stats.newThisWeek} hint="Installed in the last 7 days" icon={<PlugZap size={16} />} tone="violet" />
            <Stat label="Receiving messages" value={stats.receiving} hint="Webhook confirmed by a real message" icon={<Radio size={16} />} tone="emerald" />
            <Stat label="Uninstalled" value={stats.uninstalled} hint="Removed after installing" icon={<Unplug size={16} />} tone="amber" />
          </>
        ) : (
          [0, 1, 2, 3].map((k) => <Skeleton key={k} className="h-[7.5rem] w-full" rounded="rounded-2xl" />)
        )}
      </div>

      {stats?.byApp?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {stats.byApp.map((a) => {
            const on = query.appId === a.appId;
            return (
              <button
                key={a.appId}
                type="button"
                aria-pressed={on}
                onClick={() => setQuery({ appId: on ? "" : a.appId })}
                className={cx(
                  "inline-flex items-center gap-2 rounded-xl border py-1.5 pl-1.5 pr-3 text-[12.5px] font-medium shadow-sm transition-colors",
                  on ? "border-brand-300 bg-brand-50 text-brand-800" : "border-ink-200 bg-white text-ink-700 hover:border-ink-300",
                )}
              >
                <AppIcon app={a} size="sm" className="!h-6 !w-6 !rounded-md !text-[9px]" />
                {a.appName}
                <span className={cx("tabular-nums", on ? "text-brand-600" : "text-ink-400")}>{a.active}</span>
              </button>
            );
          })}
        </div>
      )}

      <Card padded={false}>
        <ListFilters
          search={search}
          onSearch={setSearch}
          placeholder="Search account name or ID"
          filters={[
            {
              label: "App",
              value: query.appId,
              // Plans belong to an app, so changing the app clears the plan.
              onChange: (appId) => setQuery({ appId, plan: "" }),
              options: [["", "All apps"], ...(stats?.byApp || []).map((a) => [a.appId, a.appName])],
              className: "w-48",
            },
            ...(query.appId && plansInUse.length
              ? [
                  {
                    label: "Plan",
                    value: query.plan,
                    onChange: (plan) => setQuery({ plan }),
                    options: [["", "Any plan"], ["none", "No plan"], ...plansInUse.map((p) => [p.planId, p.name || `Plan …${p.planId.slice(-6)}`])],
                    className: "w-40",
                  },
                ]
              : []),
            { label: "Status", value: query.status, onChange: (status) => setQuery({ status }), options: [["", "Any status"], ["active", "Installed"], ["uninstalled", "Uninstalled"]], className: "w-36" },
            // Sub-account apps only list location installs, so a level choice
            // means something only once an agency app exists.
            ...(stats?.hasAgencyApps || query.level
              ? [{ label: "Level", value: query.level, onChange: (level) => setQuery({ level }), options: [["", "Any level"], ["location", "Location"], ["agency", "Agency"]], className: "w-32" }]
              : []),
            { label: "Access token", value: query.token, onChange: (token) => setQuery({ token }), options: [["", "Any token"], ["connected", "Connected"], ["missing", "Missing"]], className: "w-36" },
            { label: "Credentials", value: query.credentials, onChange: (credentials) => setQuery({ credentials }), options: [["", "Any credentials"], ["saved", "Saved"], ["missing", "Not saved"]], className: "w-40" },
            { label: "Webhook", value: query.webhook, onChange: (webhook) => setQuery({ webhook }), options: [["", "Any webhook"], ["verified", "Receiving"], ["waiting", "Not receiving yet"], ["confirmed", "Confirmed only"], ["none", "Not set up"]], className: "w-44" },
            { label: "Sort", value: query.sort === "newest" ? "" : query.sort, onChange: (sort) => setQuery({ sort: sort || "newest" }), options: [["", "Newest first"], ["oldest", "Oldest first"], ["updated", "Recently changed"], ["name", "Account name"]], className: "w-40" },
          ]}
          onClear={clearFilters}
        />

        {loading ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3, 4].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-9 w-9" rounded="rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<Plug size={24} />}
            title={filtered ? "No installs match" : "No installs yet"}
            description={filtered ? "Try another app, filter or search term." : "Installs appear here as soon as someone installs one of your apps."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[56rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["Account", "App", "Status", "Setup", "Last activity", "Installed"].map((h) => (
                    <th key={h} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.items.map((i) => (
                  <tr
                    key={i._id}
                    onClick={() => setOpenId(i._id)}
                    className={cx("cursor-pointer transition-colors hover:bg-ink-50", openId === i._id && "bg-brand-50/50", !i.active && "opacity-60")}
                  >
                    <td className="max-w-[16rem] px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className={cx("grid h-8 w-8 shrink-0 place-items-center rounded-lg", i.level === "agency" ? "bg-violet-50 text-violet-600" : "bg-ink-100 text-ink-500")} title={i.level === "agency" ? "Agency" : "Location"}>
                          {i.level === "agency" ? <Building2 size={15} /> : <MapPin size={15} />}
                        </span>
                        <div className="min-w-0">
                          <button type="button" onClick={() => setOpenId(i._id)} className="block max-w-full truncate text-left font-medium text-ink-900 hover:text-brand-600">
                            {accountName(i)}
                          </button>
                          <CopyId value={accountId(i)} />
                        </div>
                      </div>
                    </td>
                    <td className="max-w-[14rem] px-5 py-3">
                      <div className="flex items-center gap-2">
                        <AppIcon app={i} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] text-ink-700">{i.appName}</span>
                          {i.plan && (
                            <span className="block truncate text-[11.5px] text-ink-400" title={i.plan.planId}>
                              {planName(i.plan)}
                              {priceText(i.plan) && ` · ${priceText(i.plan)}`}
                              {i.onTrial && " · trial"}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <StatusPill install={i} />
                        {i.active && !i.connected && <Pill tone="rose" icon={AlertTriangle}>No access token</Pill>}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        <CredentialsPill install={i} />
                        <WebhookPill install={i} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{i.lastActivityAt ? ago(i.lastActivityAt) : <span className="text-ink-300">Never</span>}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{formatDate(i.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "install" : "installs"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>

      {openId && <InstallDrawer id={openId} onClose={closeDrawer} />}
    </div>
  );
};

export default Installs;
